// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import type { User, UserForm } from "@/types/user.type";
import { baseRestQueryResult as baseQuery, showNotification } from "@/services/api";
import { ListResponse } from "@/types/response.type";

// Define a service using a base URL and expected endpoints
export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery,
  endpoints: (builder) => ({
    getUsers: builder.query<ListResponse<User>, {}>({
      query: (data) => ({url: `user`, params: data}),
      keepUnusedDataFor: 0,
      transformResponse: (rawResult: {data: ListResponse<User>}, meta) => {
        return rawResult.data
      },
    }),
    getUser: builder.query<User, string>({
      query: (id) => `user/${id}`,
      keepUnusedDataFor: 0,
      transformResponse: (rawResult: {data: User}, meta) => {
        return rawResult.data
      },
    }),
    addUser: builder.mutation<User, UserForm>({
      query: (body) => ({
        url: 'user',
        method: 'POST',
        body,
      }),
      async onQueryStarted(body, { queryFulfilled }) {
        try {
          const { data, meta } = await queryFulfilled
          // `onSuccess` side-effect
          showNotification("POST", "success")
        } catch (err: any) {
          
          // `onError` side-effect
          showNotification("POST", "error", err?.error?.data?.message || "error")
        }
    }
    }),
    editUser: builder.mutation<User, Pick<User, "id">>({
      query: ({id, ...body}) => ({
        url: `user/${id}`,
        method: 'PUT',
        body,
      }),
      async onQueryStarted(body, { queryFulfilled }) {
        try {
          const { data, meta } = await queryFulfilled
          // `onSuccess` side-effect
          showNotification("PUT", "success")
        } catch (err: any) {
          
          // `onError` side-effect
          showNotification("PUT", "error", err?.error?.data?.message || "error")
        }
    }
    }),
    deleteUser: builder.mutation<any, string>({
      query: (id) => ({
        url: `user/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(body, { queryFulfilled }) {
        try {
          const { data, meta } = await queryFulfilled
          // `onSuccess` side-effect
          showNotification("DELETE", "success")
        } catch (err: any) {
          
          // `onError` side-effect
          showNotification("DELETE", "error", err?.error?.data?.message || "error")
        }
    }
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetUsersQuery, useGetUserQuery, useAddUserMutation, useEditUserMutation, useDeleteUserMutation } = userApi;
